import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { MdEmail, MdVisibility, MdVisibilityOff, MdVpnKey } from 'react-icons/md';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  InputAdornment,
  TextField,
  CircularProgress,
} from '@mui/material';
import InputSelect from 'components/inputSelect';
import { AppLogo, LoginLogo, Container, NeedsValidation, Content } from 'styles/Styles';
import { handleApiError } from 'utils/handleApiError';
import { getBackgroundImage, getLogo, getSlugFromURL } from 'utils/whiteLabel';

import { useAuth } from '../../contexts/auth';

export default function Login() {
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [projectSelected, setProjectSelected] = useState<string | null>(null);
  const [databaseSelected, setDatabaseSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const context = useAuth();
  const reRef = useRef<ReCAPTCHA>();
  const history = useHistory();
  const PROJECT = getSlugFromURL();

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    switch (step) {
      case 0:
        await internalFirstStep(email);
        break;
      case 1:
        await internalSelectProject(projectSelected ?? '', databaseSelected ?? '');
        break;
      case 2:
        await internalLogin(email, password);
        break;
    }
  };

  useEffect(() => {
    setStep(0);
    setEmail('');
    setPassword('');
    setProjectSelected(null);
    setDatabaseSelected(null);
  }, []);

  useEffect(() => {
    if (context.listProject.length > 0) {
      if (context.listProject.length == 1) {
        internalSelectProject(context.listProject[0].projeto, context.listProject[0].banco);
      } else {
        setStep(1);
      }
    }
  }, [context.listProject]);

  async function internalFirstStep(email: string) {
    setLoading(true);

    if (email) {
      try {
        const reToken = await reRef.current?.executeAsync();
        reRef?.current?.reset();
        if (reToken) {
          const success = await context.validateEmail({ email, reToken });
          if (!success) {
            toast.error(
              <>
                <Typography fontWeight={600} color="red">
                  Falha ao tentar entrar.
                </Typography>
                <Typography component="h6" variant="subtitle2">
                  E-mail não encontrado
                </Typography>
              </>,
            );
          }
        }
      } catch (error) {
        handleApiError(error);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error(
        <>
          <Typography fontWeight={600} color="red">
            Falha ao tentar entrar.
          </Typography>
          <Typography component="h6" variant="subtitle2">
            Favor preencher o email.
          </Typography>
        </>,
      );
      setLoading(false);
    }
  }

  async function internalSelectProject(project: string, database: string) {
    setLoading(true);
    await context.selectProject(project, database);
    setStep(2);
    setLoading(false);
  }

  async function internalLogin(email: string, password: string) {
    setLoading(true);

    if (email && password) {
      try {
        const reToken = await reRef.current?.executeAsync();
        reRef?.current?.reset();

        if (reToken) {
          const success = await context.loginUser({ email, password, reToken });
          if (success) {
            toast.success(
              <>
                <Typography fontWeight={600} color="green">
                  Login feito com sucesso!
                </Typography>
                <Typography component="h6" variant="subtitle2">
                  Bem-vindo(a) ao {process.env.REACT_APP_WEBSITE_NAME}
                </Typography>
              </>,
            );
            setTimeout(() => {
              history.push('/');
            }, 2000);
          } else {
            toast.error(
              <>
                <Typography fontWeight={600} color="red">
                  Falha ao tentar entrar.
                </Typography>
                <Typography component="h6" variant="subtitle2">
                  Senha incorreta
                </Typography>
              </>,
            );
          }
        }
      } catch (error) {
        handleApiError(error);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error(
        <>
          <Typography fontWeight={600} color="red">
            Falha ao tentar entrar.
          </Typography>
          <Typography component="h6" variant="subtitle2">
            Favor preencher a senha.
          </Typography>
        </>,
      );
      setLoading(false);
    }
  }

  return (
    <Container style={{ backgroundImage: `url(${getBackgroundImage(PROJECT)})` }}>
      <Content>
        <LoginLogo>
          <AppLogo src={getLogo(PROJECT)} height="135" alt="logo" />
        </LoginLogo>

        <Card
          sx={{
            border: '1px solid #0071bc',
            borderRadius: '10px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
            '&:hover': {
              boxShadow: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
            },
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(2px)',
          }}
        >
          <CardHeader sx={{ bgcolor: '#0071bc' }} />
          <CardContent>
            <NeedsValidation onSubmit={handleSubmit}>
              <TextField
                id="input-email"
                fullWidth
                required
                disabled={step >= 1}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-mail"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MdEmail color="#666" />
                    </InputAdornment>
                  ),
                }}
              />

              {step >= 1 && context?.listProject?.length > 1 && (
                <InputSelect
                  disabled={step >= 2}
                  name={'projetos'}
                  fieldid={'projeto'}
                  options={context.listProject}
                  getOptionLabel={(option: any) => option?.empresa ?? ''}
                  onSelectedValue={async (item) => {
                    if (item) {
                      setProjectSelected(item.projeto);
                      setDatabaseSelected(item.banco);
                    } else {
                      setProjectSelected(null);
                      setDatabaseSelected(null);
                    }
                  }}
                  renderOption={(props: any, option: any) => (
                    <span {...props} key={option.projeto}>
                      {option.empresa}
                    </span>
                  )}
                  renderInput={(params: any) => (
                    <TextField {...params} label={'Projetos'} variant="outlined" />
                  )}
                />
              )}

              {step === 2 && (
                <TextField
                  id="input-password"
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha"
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdVpnKey color="#666" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <MdVisibility color="#666" />
                          ) : (
                            <MdVisibilityOff color="#666" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <div className="gap-2 d-grid">
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ borderRadius: '8px' }}
                >
                  {!loading ? (
                    <Typography fontSize="16px" fontWeight={600} letterSpacing="0.01em">
                      {step === 2 ? 'ENTRAR' : 'PRÓXIMO'}
                    </Typography>
                  ) : (
                    <CircularProgress color="inherit" size={24} />
                  )}
                </Button>
              </div>
            </NeedsValidation>
          </CardContent>
          <CardActions sx={{ display: 'flex', justifyContent: 'center', paddingBottom: '16px' }}>
            <Link to="/recover-password">
              <Typography textAlign="center">Esqueceu sua senha?</Typography>
            </Link>
          </CardActions>
        </Card>
        <Card
          sx={{
            paddingBottom: 0,
            border: '1px solid #0071bc',
            borderRadius: '10px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
            '&:hover': {
              boxShadow: '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
            },
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(2px)',
          }}
        >
          <CardContent sx={{ padding: '14px !important' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography color="#666" fontWeight={600} textAlign="center" width="100%">
                Novo Usuário?
              </Typography>
              <Link to="/register">
                <Button
                  color="secondary"
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ whiteSpace: 'nowrap', borderRadius: '8px' }}
                >
                  <Typography color="#fff" fontSize="16px" fontWeight={600} letterSpacing="0.01em">
                    TESTE GRÁTIS!
                  </Typography>
                </Button>
              </Link>
            </Box>
          </CardContent>
        </Card>

        <Box display="flex" alignItems="center" justifyContent="space-evenly">
          <Link target={'_blank'} to={'./' + PROJECT + '/files/TERMOS-DE-USO.pdf'} rel="noreferrer">
            <Typography
              fontSize="15px"
              color="#fff"
              sx={{
                '&:hover': {
                  fontWeight: 700,
                },
              }}
            >
              Termos
            </Typography>
          </Link>

          <Link
            target={'_blank'}
            to={'./' + PROJECT + '/files/POLITICA-DE-PRIVACIDADE.html'}
            rel="noreferrer"
          >
            <Typography
              fontSize="15px"
              color="#fff"
              sx={{
                '&:hover': {
                  fontWeight: 700,
                },
              }}
            >
              Privacidade
            </Typography>
          </Link>
        </Box>
      </Content>

      <ReCAPTCHA
        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
        size="invisible"
        ref={reRef as React.RefObject<ReCAPTCHA>}
      />
    </Container>
  );
}
